/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/ActivityFeedItem.tsx
 * PURPOSE: Single timeline row for ActivityFeed — type-specific icons and colours; solid at 50% opacity when complete; overdue uses red alert icon
 * DEPENDENCIES: @heroicons/react/24/outline, @heroicons/react/24/solid, flowbite-react, lucide-react, tailwind-merge, ../../context/CrmActionsContext, ../../types/activity.types, ../../utils/formatActivityRelativeTime, ../call/CallRecordingCard, ../shared/OwnerAvatar, ../shared/StatusBadge
 * PUBLIC_API: ActivityFeedItem
 * SIDE_EFFECTS: Invokes onMarkComplete when user clicks Mark complete; delete calls **`useCrmActions().deleteActivity`**
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 142/200
 * RECENT: 2026-05-07 Call rows use **`CallRecordingCard`** (scrubber, speed, summary); green **`PhoneCall`** icon — root **`CHANGELOG` [2.2.858]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.738] overflow **Delete** → **`deleteActivity`** — CHANGELOG [2.2.738]
 */

import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  CalendarIcon as CalendarIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
} from '@heroicons/react/24/solid';
import { Button, Dropdown, DropdownItem, Spinner } from 'flowbite-react';
import { MoreHorizontal, PhoneCall } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useCrmActions } from '../../context/CrmActionsContext';
import type { Activity, ActivityType } from '../../types/activity.types';
import { formatActivityRelativeTime } from '../../utils/formatActivityRelativeTime';
import { OwnerAvatar } from '../shared/OwnerAvatar';
import { StatusBadge } from '../shared/StatusBadge';
import { CallRecordingCard } from '../call/CallRecordingCard';

function TypeIcon({ type, complete, overdue }: { type: ActivityType; complete: boolean; overdue: boolean }) {
  if (overdue) {
    return <ExclamationCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden />;
  }

  const wrap = complete ? 'opacity-50' : '';

  if (type === 'call') {
    return (
      <PhoneCall className={twMerge('h-4 w-4 text-green-600 dark:text-green-400', wrap)} aria-hidden />
    );
  }
  if (type === 'email') {
    const Icon = complete ? EnvelopeIconSolid : EnvelopeIcon;
    return <Icon className={`h-4 w-4 text-blue-600 dark:text-blue-400 ${wrap}`} aria-hidden />;
  }
  if (type === 'meeting') {
    const Icon = complete ? CalendarIconSolid : CalendarIcon;
    return <Icon className={`h-4 w-4 text-purple-600 dark:text-purple-400 ${wrap}`} aria-hidden />;
  }
  if (type === 'note') {
    const Icon = complete ? DocumentTextIconSolid : DocumentTextIcon;
    return <Icon className={`h-4 w-4 text-amber-600 dark:text-amber-400 ${wrap}`} aria-hidden />;
  }
  const Icon = complete ? CheckCircleIconSolid : CheckCircleIcon;
  return <Icon className={`h-4 w-4 text-green-600 dark:text-green-400 ${wrap}`} aria-hidden />;
}

export type ActivityFeedItemProps = {
  activity: Activity;
  isLast: boolean;
  completing: boolean;
  onMarkComplete: () => void;
};

export function ActivityFeedItem({ activity, isLast, completing, onMarkComplete }: ActivityFeedItemProps) {
  const actions = useCrmActions();
  const readOnly = activity.readOnly === true;
  const showComplete = !readOnly && activity.status !== 'complete';
  const rel = formatActivityRelativeTime(activity.createdAtISO);
  const complete = activity.status === 'complete';
  const overdue = activity.status === 'overdue';
  return (
    <li className="flex gap-3" data-testid={`activity-feed-item-${activity.id}`}>
      <div className="flex w-10 shrink-0 flex-col items-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-200 bg-gray-50 shadow-sm dark:border-gray-600 dark:bg-gray-800">
          <TypeIcon type={activity.type} complete={complete} overdue={overdue} />
        </div>
        {!isLast ? <div className="mt-1 w-px flex-1 bg-gray-200 dark:bg-gray-600" aria-hidden /> : null}
      </div>
      <div className={`relative min-w-0 flex-1 ${isLast ? '' : 'pb-8'}`}>
        <div className="flex flex-wrap items-start justify-between gap-2 pr-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{activity.subject}</p>
            <StatusBadge status={activity.status} size="sm" />
            {activity.timelineSource === 'revops' ? (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">
                RevOps
              </span>
            ) : null}
          </div>
          {!readOnly ? (
          <Dropdown
            inline
            size="sm"
            label=""
            placement="bottom-end"
            renderTrigger={() => (
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Activity actions"
                data-testid={`activity-feed-item-menu-${activity.id}`}
              >
                <MoreHorizontal className="h-5 w-5" aria-hidden />
              </button>
            )}
          >
            <DropdownItem
              className="text-red-600 dark:text-red-400"
              data-testid={`activity-feed-item-delete-${activity.id}`}
              onClick={() => void actions.deleteActivity(activity.id)}
            >
              Delete
            </DropdownItem>
          </Dropdown>
          ) : null}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          <OwnerAvatar name={activity.ownerName} avatarUrl={activity.ownerAvatarUrl} size="xs" showName />
          <span aria-hidden>·</span>
          <time dateTime={activity.createdAtISO}>{rel}</time>
        </div>
        {activity.type === 'call' ? <CallRecordingCard activity={activity} /> : null}
        {activity.type !== 'call' && activity.notes ? (
          <p className="mt-2 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">{activity.notes}</p>
        ) : null}
        {showComplete ? (
          <div className="mt-3">
            <Button type="button" size="xs" color="light" disabled={completing} onClick={onMarkComplete}>
              {completing ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Marking…
                </>
              ) : (
                'Mark complete'
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
